import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Tooltip, CircularProgress, Snackbar, Alert, Chip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Refresh as RefreshIcon, Factory as FactoryIcon, Close as CloseIcon } from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../../config/environment';
import { usePageBreakpoints, pageContainerSx } from '../../hooks/useClinicPageBreakpoints';


const emptyForm = { name: '', cnpj: '', phone: '', email: '', contact_name: '', address: '', website: '', notes: '' };

const InventoryManufacturers: React.FC = () => {
  const { isMobile } = usePageBreakpoints();

  const [manufacturers, setManufacturers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(getApiUrl('/inventory-module/manufacturers')); setManufacturers(res.data.data || []); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const openDialog = (m?: any) => {
    if (m) {
      setEditingId(m.id);
      setForm({ name: m.name || '', cnpj: m.cnpj || '', phone: m.phone || '', email: m.email || '', contact_name: m.contact_name || '', address: m.address || '', website: m.website || '', notes: m.notes || '' });
    } else { setEditingId(null); setForm({ ...emptyForm }); }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setSnackbar({ open: true, message: 'Nome obrigatório', severity: 'error' }); return; }
    try {
      setSaving(true);
      if (editingId) await axios.put(getApiUrl(`/inventory-module/manufacturers/${editingId}`), form);
      else await axios.post(getApiUrl('/inventory-module/manufacturers'), form);
      setSnackbar({ open: true, message: editingId ? 'Atualizado!' : 'Criado!', severity: 'success' });
      setDialogOpen(false); fetch();
    } catch (e: any) { setSnackbar({ open: true, message: e.response?.data?.error || 'Erro', severity: 'error' }); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Excluir permanentemente este fabricante?\n\nItens de estoque que usavam este fabricante ficarão sem vínculo (fabricante em branco).')) return;
    try {
      await axios.delete(getApiUrl(`/inventory-module/manufacturers/${id}`));
      setSnackbar({ open: true, message: 'Fabricante excluído.', severity: 'success' });
      fetch();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.error || 'Não foi possível excluir', severity: 'error' });
    }
  };

  return (
    <Box sx={pageContainerSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FactoryIcon sx={{ fontSize: 32, color: '#8b5cf6' }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Fabricantes de Produto</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Atualizar"><IconButton onClick={fetch}><RefreshIcon /></IconButton></Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}
            sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' }, textTransform: 'none', fontWeight: 600 }}>
            Novo Fabricante
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: '#8b5cf6' }} /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f9fafb' }}>
                <TableCell sx={{ fontWeight: 700 }}>Fabricante</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>CNPJ</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Contato</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Telefone</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {manufacturers.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: '#9ca3af' }}>Nenhum fabricante cadastrado</TableCell></TableRow>
              ) : manufacturers.map(m => (
                <TableRow key={m.id} hover>
                  <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{m.name}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{m.cnpj || '-'}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{m.contact_name || '-'}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{m.phone || '-'}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{m.email || '-'}</Typography></TableCell>
                  <TableCell align="center">
                    <Chip label={m.is_active ? 'Ativo' : 'Inativo'} size="small" sx={{ bgcolor: m.is_active ? '#d1fae5' : '#f3f4f6', color: m.is_active ? '#059669' : '#6b7280', fontWeight: 600 }} />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar"><IconButton size="small" onClick={() => openDialog(m)}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                    <Tooltip title="Excluir permanentemente"><IconButton size="small" onClick={() => handleDelete(m.id)}><DeleteIcon sx={{ fontSize: 16, color: '#ef4444' }} /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog fullScreen={isMobile} scroll="paper" open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between' }}>
          {editingId ? 'Editar Fabricante' : 'Novo Fabricante'}
          <IconButton onClick={() => setDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}><TextField fullWidth required label="Nome *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="CNPJ" value={form.cnpj} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Nome do Contato" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Telefone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} size="small" /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Endereço" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} size="small" /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Observações" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} size="small" multiline rows={2} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' }, textTransform: 'none', fontWeight: 600 }}>
            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default InventoryManufacturers;
